"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslations } from "use-intl";

import { FavoriteIcon } from "@/components/appIcons";
import { Button } from "@/components/ui/button";
import { useViewerQueryState } from "@/hooks/use-viewer-query-state";
import { cn } from "@/lib/utils";
import { client } from "@/utils/orpc.client";

import { getArtistFollowStatusQueryOptions } from "./artist-follow-query";

interface ArtistFollowButtonProps {
  artistId: string;
  artistName: string;
  className?: string;
}

export function ArtistFollowButton({ artistId, artistName, className }: ArtistFollowButtonProps) {
  const t = useTranslations("shared.social");
  const queryClient = useQueryClient();
  const { session, viewerId, enabledWhenAuthenticated } = useViewerQueryState();
  const statusQueryOptions = getArtistFollowStatusQueryOptions(artistId, {
    viewerId,
    enabled: enabledWhenAuthenticated,
  });
  const [isHovering, setIsHovering] = useState(false);

  const { data: status } = useQuery(statusQueryOptions);

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: statusQueryOptions.queryKey,
    });
  };

  const followMutation = useMutation({
    mutationFn: () => client.artistFollows.follow({ artistId, artistName }),
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: statusQueryOptions.queryKey,
      });
      queryClient.setQueryData(statusQueryOptions.queryKey, {
        isFollowing: true,
      });
    },
    onSettled: invalidate,
  });

  const unfollowMutation = useMutation({
    mutationFn: () => client.artistFollows.unfollow({ artistId }),
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: statusQueryOptions.queryKey,
      });
      queryClient.setQueryData(statusQueryOptions.queryKey, {
        isFollowing: false,
      });
    },
    onSettled: invalidate,
  });

  const isFollowing = status?.isFollowing ?? false;
  const isPending = followMutation.isPending || unfollowMutation.isPending;

  const handleClick = () => {
    if (!session) return;
    if (isFollowing) {
      unfollowMutation.mutate();
    } else {
      followMutation.mutate();
    }
  };

  const label = isFollowing ? (isHovering ? t("unfollow") : t("following")) : t("follow");

  return (
    <Button
      variant={isFollowing ? "outline" : "default"}
      size="sm"
      onClick={handleClick}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      disabled={!session || isPending}
      className={cn(
        "min-w-[100px]",
        isFollowing &&
          isHovering &&
          "border-destructive/50 text-destructive hover:bg-destructive/10",
        className,
      )}
    >
      <FavoriteIcon className="text-[18px]" />
      {label}
    </Button>
  );
}
